import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, Download, MonitorDown, Trash2, UserX } from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  ACTIVITY_RETENTION_DAYS,
  API_PREFIX,
  PASSWORD_MIN_LENGTH,
  RETENTION_CHOICES,
  addDays,
  formatCalendarDate,
  formatDateTime,
  ukDate,
  type RetentionDays,
} from '@findmy/shared';
import {
  useActivity,
  useChangePassword,
  useDeleteAccount,
  useDeleteHistory,
  useSettings,
  useUpdateSettings,
} from '../api/queries';
import { Dialog } from '../components/Dialog';
import { TwoFactorSection } from '../components/TwoFactor';
import { Button, ErrorText, Spinner } from '../components/ui';
import { describeActivity, isWarning } from '../lib/activity';
import { isInstalledApp, useInstallPrompt } from '../lib/install';
import { useNow } from '../lib/useNow';

const inputClass =
  'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800';

const retentionLabel = (days: number) => (days === 365 ? '1 year' : `${days} days`);

function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className="space-y-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800"
    >
      <h2 id={id} className="text-base font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

const Hint = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-slate-600 dark:text-slate-400">{children}</p>
);

function RetentionSetting({ current }: { current: number }) {
  const update = useUpdateSettings();
  const now = useNow();
  const [pending, setPending] = useState<RetentionDays | null>(null);

  const choose = (days: RetentionDays) => {
    if (days === current) return;
    // Shortening deletes history straight away, so it's confirmed first.
    if (days < current) setPending(days);
    else update.mutate({ retentionDays: days });
  };

  return (
    <>
      <label className="block text-sm font-medium">
        Keep location history for
        <select
          value={current}
          onChange={(e) => choose(Number(e.target.value) as RetentionDays)}
          disabled={update.isPending}
          className={`${inputClass} sm:w-48`}
        >
          {RETENTION_CHOICES.map((days) => (
            <option key={days} value={days}>
              {retentionLabel(days)}
            </option>
          ))}
        </select>
      </label>
      <Hint>
        Older locations are deleted every night at 03:30. Each device’s last known location is always kept, so
        the map can still show where it was last seen.
      </Hint>
      {update.error && <ErrorText>{update.error.message}</ErrorText>}

      <Dialog open={pending !== null} onClose={() => setPending(null)} title="Delete older history?">
        {pending !== null && (
          <div className="space-y-4">
            <p className="text-sm">
              Locations recorded before <strong>{formatCalendarDate(addDays(ukDate(now), -pending))}</strong>{' '}
              will be deleted now, and history will be kept for {retentionLabel(pending)} from now on. This
              can’t be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate({ retentionDays: pending }, { onSuccess: () => setPending(null) })
                }
              >
                Delete older history
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}

function AddressSetting({ enabled, available }: { enabled: boolean; available: boolean }) {
  const update = useUpdateSettings();
  return (
    <>
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={available && enabled}
          disabled={!available || update.isPending}
          onChange={(e) => update.mutate({ reverseGeocodeEnabled: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-blue-600"
        />
        <span>
          <span className="font-medium">Show approximate street addresses</span>
          <span className="block text-slate-600 dark:text-slate-400">
            {available
              ? 'Your server looks them up from OpenStreetMap’s Nominatim service, sending only the position being shown (to about 100 m). Results are cached for 30 days.'
              : 'Address lookups are switched off on this server (REVERSE_GEOCODING=false).'}
          </span>
        </span>
      </label>
      {update.error && <ErrorText>{update.error.message}</ErrorText>}
    </>
  );
}

function LostContactSetting({ current }: { current: string }) {
  const update = useUpdateSettings();
  const [saved, setSaved] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const contact = String(new FormData(event.currentTarget).get('contact'));
    update.mutate({ defaultLostContact: contact }, { onSuccess: () => setSaved(true) });
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <label className="block text-sm font-medium">
        How to reach you
        <input
          name="contact"
          maxLength={120}
          defaultValue={current}
          onChange={() => setSaved(false)}
          placeholder="e.g. 07700 900123 or you@example.com"
          className={inputClass}
        />
      </label>
      <Hint>
        Filled in for you when you turn on lost mode. Shown on the lost device only while it’s in lost mode.
      </Hint>
      <div className="flex items-center gap-3">
        <Button type="submit" variant="primary" disabled={update.isPending}>
          Save
        </Button>
        {saved && <span className="text-sm text-emerald-700 dark:text-emerald-400">Saved</span>}
      </div>
      {update.error && <ErrorText>{update.error.message}</ErrorText>}
    </form>
  );
}

function PasswordSection() {
  const change = useChangePassword();
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const newPassword = String(form.get('newPassword'));
    setDone(false);
    if (newPassword !== String(form.get('repeatPassword'))) return setMismatch(true);
    setMismatch(false);
    change.mutate(
      { currentPassword: String(form.get('currentPassword')), newPassword },
      {
        onSuccess: () => {
          formElement.reset();
          setDone(true);
        },
      },
    );
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <label className="block text-sm font-medium">
        Current password
        <input
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          required
          className={inputClass}
        />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm font-medium">
          New password
          <input
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            className={inputClass}
          />
        </label>
        <label className="block text-sm font-medium">
          Repeat new password
          <input
            name="repeatPassword"
            type="password"
            autoComplete="new-password"
            required
            className={inputClass}
          />
        </label>
      </div>
      <Hint>
        At least {PASSWORD_MIN_LENGTH} characters. Changing it logs you out everywhere else (other browsers
        and phones); this one stays logged in. Paired devices aren’t affected.
      </Hint>
      <div className="flex items-center gap-3">
        <Button type="submit" variant="primary" disabled={change.isPending}>
          Change password
        </Button>
        {done && <span className="text-sm text-emerald-700 dark:text-emerald-400">Password changed</span>}
      </div>
      {mismatch && <ErrorText>The new passwords don’t match.</ErrorText>}
      {change.error && <ErrorText>{change.error.message}</ErrorText>}
    </form>
  );
}

/** Install the dashboard as an app: the browser's own dialog where it has one, else how to. */
function InstallSection() {
  const { canInstall, install } = useInstallPrompt();
  return (
    <>
      <Hint>
        Install the dashboard on this phone, tablet or computer to open it from its own icon, full screen,
        like any other app. It still needs to reach your server, through Tailscale.
      </Hint>
      {canInstall ? (
        <Button variant="primary" onClick={() => void install()}>
          <MonitorDown className="h-4 w-4" aria-hidden /> Install app
        </Button>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-400">
          <li>
            <strong>Android, in Chrome:</strong> the ⋮ menu → <strong>Install app</strong> (or{' '}
            <strong>Add to Home screen</strong>).
          </li>
          <li>
            <strong>Android, in Firefox:</strong> the ⋮ menu → <strong>Add app to Home screen</strong>.
          </li>
          <li>
            <strong>Computer, in Chrome or Edge:</strong> the install icon at the right of the address bar, or
            the menu → <strong>Install Find My</strong>.
          </li>
          <li>
            <strong>iPhone or iPad, in Safari:</strong> Share → <strong>Add to Home Screen</strong>.
          </li>
        </ul>
      )}
    </>
  );
}

function ExportSection() {
  const linkClass =
    'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800';
  return (
    <>
      <Hint>
        Download a copy of everything stored about your account. <strong>JSON</strong> has your settings,
        devices, every location, commands and security activity. <strong>GPX</strong> has the locations only,
        one track per device, for map and GPS apps.
      </Hint>
      <div className="flex flex-wrap gap-2">
        <a href={`${API_PREFIX}/export?format=json`} className={linkClass}>
          <Download className="h-4 w-4" aria-hidden /> Download JSON
        </a>
        <a href={`${API_PREFIX}/export?format=gpx`} className={linkClass}>
          <Download className="h-4 w-4" aria-hidden /> Download GPX
        </a>
      </div>
    </>
  );
}

function ActivitySection() {
  const { data: events, isPending, error } = useActivity();
  return (
    <>
      <Hint>
        Logins, pairings, commands, exports and changes to your account, kept for {ACTIVITY_RETENTION_DAYS}{' '}
        days.
      </Hint>
      {isPending && <Spinner />}
      {error && <ErrorText>{error.message}</ErrorText>}
      {events && (
        <ul className="max-h-80 divide-y divide-slate-200 overflow-y-auto text-sm dark:divide-slate-800">
          {events.map((event) => (
            <li key={event.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2">
              <span className="w-32 shrink-0 text-xs text-slate-500 tabular-nums">
                {formatDateTime(event.createdAt)}
              </span>
              <span
                className={`flex-1 ${isWarning(event) ? 'font-medium text-amber-700 dark:text-amber-400' : ''}`}
              >
                {describeActivity(event)}
              </span>
              {event.ip && <span className="text-xs text-slate-500 tabular-nums">{event.ip}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Asks for the password before something that can't be undone. */
function PasswordConfirm({
  open,
  onClose,
  title,
  children,
  action,
  pending,
  error,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  action: string;
  pending: boolean;
  error: Error | null;
  onConfirm: (password: string) => void;
}) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onConfirm(String(new FormData(event.currentTarget).get('password')));
  };
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2 text-sm">{children}</div>
        <label className="block text-sm font-medium">
          Your password
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            className={inputClass}
          />
        </label>
        {error && <ErrorText>{error.message}</ErrorText>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={pending}>
            {action}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DangerZone() {
  const navigate = useNavigate();
  const deleteHistory = useDeleteHistory();
  const deleteAccount = useDeleteAccount();
  const [open, setOpen] = useState<'history' | 'account' | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const close = () => {
    setOpen(null);
    deleteHistory.reset();
    deleteAccount.reset();
  };

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Hint>Delete every location from every device. Devices stay paired and keep sharing.</Hint>
        <Button variant="ghostDanger" onClick={() => setOpen('history')} className="shrink-0">
          <Trash2 className="h-4 w-4" aria-hidden /> Delete all history
        </Button>
      </div>
      {result && <p className="text-sm text-emerald-700 dark:text-emerald-400">{result}</p>}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Hint>Delete your account, your devices and all their history. Your devices are unpaired.</Hint>
        <Button variant="danger" onClick={() => setOpen('account')} className="shrink-0">
          <UserX className="h-4 w-4" aria-hidden /> Delete account
        </Button>
      </div>

      <PasswordConfirm
        open={open === 'history'}
        onClose={close}
        title="Delete all location history?"
        action="Delete all history"
        pending={deleteHistory.isPending}
        error={deleteHistory.error}
        onConfirm={(password) =>
          deleteHistory.mutate(password, {
            onSuccess: ({ deleted }) => {
              setResult(
                `Deleted ${deleted.toLocaleString('en-GB')} ${deleted === 1 ? 'location' : 'locations'}.`,
              );
              close();
            },
          })
        }
      >
        <p>
          Every location from every device is deleted, including their last known locations. This can’t be
          undone.
        </p>
        <p>To keep a copy, download an export first.</p>
      </PasswordConfirm>

      <PasswordConfirm
        open={open === 'account'}
        onClose={close}
        title="Delete your account?"
        action="Delete my account"
        pending={deleteAccount.isPending}
        error={deleteAccount.error}
        onConfirm={(password) =>
          deleteAccount.mutate(password, {
            onSuccess: () =>
              navigate('/login', {
                replace: true,
                state: { notice: 'Your account and all its data have been deleted.' },
              }),
          })
        }
      >
        <p className="flex gap-2 font-medium text-red-700 dark:text-red-400">
          <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
          Everything goes: your account, every device and all their history. This can’t be undone.
        </p>
        <p>
          Connected devices are told straight away and delete their credentials. Others find out next time
          they check in.
        </p>
        <p>To keep a copy, download an export first.</p>
      </PasswordConfirm>
    </>
  );
}

export function SettingsPage() {
  const { data: settings, isPending, error } = useSettings();
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <h1 className="text-xl font-semibold">Settings</h1>
        {!isInstalledApp() && (
          <Section title="Install as an app">
            <InstallSection />
          </Section>
        )}
        {isPending && <Spinner />}
        {error && <ErrorText>{error.message}</ErrorText>}
        {settings && (
          <>
            <Section title="Location history">
              <RetentionSetting current={settings.retentionDays} />
            </Section>
            <Section title="Addresses">
              <AddressSetting
                enabled={settings.reverseGeocodeEnabled}
                available={settings.reverseGeocodeAvailable}
              />
            </Section>
            <Section title="Lost mode">
              <LostContactSetting current={settings.defaultLostContact} />
            </Section>
          </>
        )}
        <Section title="Password">
          <PasswordSection />
        </Section>
        {settings && (
          <Section title="Two-factor login">
            <TwoFactorSection enabled={settings.twoFactorEnabled} codesLeft={settings.recoveryCodesLeft} />
          </Section>
        )}
        <Section title="Export your data">
          <ExportSection />
        </Section>
        <Section title="Security activity">
          <ActivitySection />
        </Section>
        <Section title="Delete data">
          <DangerZone />
        </Section>
      </div>
    </div>
  );
}
